import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { isPasswordStrong } from '../utils/passwordPolicy';
import PasswordStrengthChecklist from '../components/PasswordStrengthChecklist';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get('token') || '';

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }
    api.validateResetToken(token)
      .then((res) => {
        setTokenValid(!!res.data.valid);
        setTokenEmail(res.data.email || '');
      })
      .catch(() => setTokenValid(false))
      .finally(() => setValidating(false));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordStrong(pw)) {
      toast.error('Lösenordet uppfyller inte kraven');
      return;
    }
    if (pw !== pw2) {
      toast.error('Lösenorden matchar inte');
      return;
    }
    setSubmitting(true);
    try {
      await api.resetPassword({ token, new_password: pw });
      setDone(true);
      // Försök auto-login så vi kan trigga Stripe Checkout direkt om kunden är paying
      try {
        await login(tokenEmail, pw);
        const checkoutRes = await api.postActivationCheckout(window.location.origin);
        if (checkoutRes?.data?.url) {
          // Visa kort meddelande och skicka till Stripe Checkout
          toast.success('Lösenord sparat! Slutför aktivering med kortuppgifter…');
          setTimeout(() => { window.location.href = checkoutRes.data.url; }, 1200);
          return;
        }
        // Inget Stripe-flöde behövs (demo eller redan aktiv) → till dashboarden
        setTimeout(() => { window.location.href = '/dashboard'; }, 1500);
      } catch {
        // Auto-login eller checkout misslyckades — fall tillbaka till login-sidan
        setTimeout(() => navigate('/login'), 2500);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte byta lösenord');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-12" style={{ backgroundColor: '#F7F4EF' }} data-testid="reset-password-page">
      <SEO title="Sätt nytt lösenord | Operio" canonical="/aterstall-losenord" noindex />
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-[#E8E2D9] p-8 shadow-sm">
          {validating ? (
            <div className="text-center py-10">
              <Loader2 className="w-8 h-8 mx-auto text-[#E8613A] animate-spin" />
              <p className="text-sm text-[#6B6560] mt-3">Validerar länk…</p>
            </div>
          ) : !tokenValid ? (
            <div className="text-center" data-testid="reset-invalid">
              <div className="w-14 h-14 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle size={28} className="text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#1A1A1A] mt-5" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Ogiltig eller utgången länk
              </h1>
              <p className="text-sm text-[#6B6560] mt-2">
                Länken är inte längre giltig. Begär en ny återställning.
              </p>
              <Link to="/glomt-losenord" className="inline-block mt-6">
                <Button className="bg-[#E8613A] hover:bg-[#D4532F] text-white rounded-full">Begär ny länk</Button>
              </Link>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-14 h-14 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 size={28} className="text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-[#1A1A1A] mt-5" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Lösenordet är uppdaterat!
              </h1>
              <p className="text-sm text-[#6B6560] mt-2">Du loggas in om ett ögonblick…</p>
            </div>
          ) : (
            <>
              <div className="w-12 h-12 bg-[#E8613A]/10 rounded-xl flex items-center justify-center mb-5">
                <Lock size={22} className="text-[#E8613A]" />
              </div>
              <h1 className="text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Sätt nytt lösenord
              </h1>
              <p className="text-sm text-[#6B6560] mt-2">
                För <strong>{tokenEmail}</strong>
              </p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div>
                  <Label className="text-xs">Nytt lösenord</Label>
                  <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus required className="mt-1.5 h-11" data-testid="reset-pw-1" />
                  <PasswordStrengthChecklist password={pw} />
                </div>
                <div>
                  <Label className="text-xs">Upprepa lösenord</Label>
                  <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required className="mt-1.5 h-11" data-testid="reset-pw-2" />
                  {pw2 && pw !== pw2 && (
                    <p className="text-xs text-red-600 mt-1">Lösenorden matchar inte</p>
                  )}
                </div>
                <Button type="submit" disabled={submitting || !isPasswordStrong(pw) || pw !== pw2}
                        className="w-full bg-[#E8613A] hover:bg-[#D4532F] text-white h-11 rounded-full" data-testid="reset-submit-btn">
                  {submitting ? 'Sparar…' : 'Spara nytt lösenord'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
